import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Edit2, Sparkles, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", category: "Guides", tags: [], read_time: 4, published: true };

export default function AdminBlogPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [roadmap, setRoadmap] = useState(null);
  const [genBusy, setGenBusy] = useState(null);
  const [heroBusy, setHeroBusy] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/admin/blog").then((r) => setItems(r.data)),
      api.get("/admin/blog/roadmap").then((r) => setRoadmap(r.data)),
    ]);
  useEffect(() => {
    load();
  }, []);

  const generateNext = async (tier) => {
    setGenBusy(tier || "any");
    toast.message(`Generating next ${tier || "post"} via Claude + Nano Banana — this can take 60-90s…`);
    try {
      const r = await api.post(`/admin/blog/auto-generate${tier ? `?tier=${tier}` : ""}`);
      toast.success(`Published: ${r.data.title}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenBusy(null);
    }
  };

  const regenHero = async (postId) => {
    setHeroBusy(postId);
    toast.message("Regenerating hero image…");
    try {
      await api.post(`/admin/blog/${postId}/regenerate-hero`);
      toast.success("New hero generated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hero regen failed");
    } finally {
      setHeroBusy(null);
    }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
        read_time: Number(form.read_time) || 4,
      };
      if (editing === "new") await api.post("/admin/blog", payload);
      else await api.put(`/admin/blog/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-blog">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl font-bold text-[#0A1628]">Blog CMS</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => generateNext("pillar")}
            disabled={genBusy !== null}
            data-testid="generate-pillar"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {genBusy === "pillar" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate next pillar
          </button>
          <button
            onClick={() => generateNext("spoke")}
            disabled={genBusy !== null}
            data-testid="generate-spoke"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {genBusy === "spoke" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate next spoke
          </button>
          <button
            onClick={() => {
              setEditing("new");
              setForm(EMPTY);
            }}
            data-testid="blog-add"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#0A1628] text-white text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      {/* SEO Roadmap */}
      {roadmap && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-6" data-testid="blog-roadmap">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="font-bold text-slate-900">SEO roadmap</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Auto-publishes 1 spoke every 3 days. Pillars are admin-triggered.
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-700 font-mono">
                {roadmap.published}<span className="text-slate-400">/{roadmap.total}</span>
              </div>
              <div className="text-xs text-slate-500">published</div>
            </div>
          </div>

          {/* Scheduler status strip */}
          {roadmap.scheduler && (
            <SchedulerBadge
              scheduler={roadmap.scheduler}
              nextUp={roadmap.upcoming?.[0]}
            />
          )}

          <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-5">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 rounded-full transition-all"
              style={{ width: `${(roadmap.published / Math.max(roadmap.total, 1)) * 100}%` }}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Tile label="Pillars" done={roadmap.pillars_done} total={roadmap.pillars_total} color="text-emerald-700" />
            <Tile label="Spokes" done={roadmap.spokes_done} total={roadmap.spokes_total} color="text-sky-700" />
            <Tile label="Remaining" done={roadmap.remaining} total={roadmap.total} color="text-amber-600" />
            <Tile label="Clusters" done={roadmap.clusters.filter((c) => c.pillar_published).length} total={roadmap.clusters.length} color="text-violet-600" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Up next</div>
            <ul className="space-y-1.5">
              {roadmap.upcoming.slice(0, 6).map((t) => (
                <li key={t.slug} className="flex items-center justify-between gap-2 text-sm" data-testid={`upcoming-${t.slug}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${t.tier === "pillar" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                      {t.tier}
                    </span>
                    <span className="truncate text-slate-700">{t.title}</span>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap hidden md:inline">{t.cluster}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {editing && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 soft-shadow mb-5" data-testid="blog-editor">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title">
              <input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <Field label="Slug">
              <input className="inp font-mono-display" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} />
            </Field>
            <Field label="Category">
              <input className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
            <Field label="Read time (min)">
              <input type="number" className="inp" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} />
            </Field>
          </div>
          <Field label="Excerpt" className="mt-3">
            <input className="inp" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </Field>
          <Field label="Content (Markdown supported: ## h2, ### h3, - lists)" className="mt-3">
            <textarea rows={10} className="inp h-auto py-3 resize-none" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          </Field>
          <Field label="Tags (comma separated)" className="mt-3">
            <input className="inp" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </Field>
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#0A1628]">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
          </label>
          <div className="mt-4 flex items-center gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg border border-[#E5E7EB] text-sm">Cancel</button>
            <button onClick={save} data-testid="blog-save" className="h-10 px-4 rounded-lg bg-[#0052FF] text-white text-sm font-semibold">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden soft-shadow">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#9CA3AF]">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Title</th>
              <th className="text-left px-5 py-3 font-semibold">Slug</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Views</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#F3F4F6]" data-testid={`blog-row-${p.slug}`}>
                <td className="px-5 py-3 text-[#0A1628] font-medium">{p.title}</td>
                <td className="px-5 py-3 font-mono-display text-xs text-[#4B5563]">{p.slug}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
                    {p.published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right font-mono-display text-[#0A1628]">{p.views || 0}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => regenHero(p.id)}
                    disabled={heroBusy === p.id}
                    title="Regenerate hero image"
                    data-testid={`regen-hero-${p.slug}`}
                    className="text-fuchsia-600 hover:text-fuchsia-800 mr-2 disabled:opacity-40"
                  >
                    {heroBusy === p.id ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <ImageIcon className="w-4 h-4 inline" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(p.id);
                      setForm({ ...p, tags: p.tags?.join(", ") || "" });
                    }}
                    className="text-[#0052FF] hover:text-[#0040CC] mr-2"
                  >
                    <Edit2 className="w-4 h-4 inline" />
                  </button>
                  <button onClick={() => del(p.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="py-12 text-center text-[#9CA3AF]">No posts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .inp { width:100%; height:42px; padding: 0 14px; border-radius: 8px; border: 1px solid #E5E7EB; font-size: 14px; outline: none; }
        .inp:focus { box-shadow: 0 0 0 2px #0052FF; border-color: transparent; }
      `}</style>
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Tile({ label, done, total, color }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3 text-center">
      <div className={`text-2xl font-bold font-mono ${color}`}>
        {done}<span className="text-slate-400 text-base">/{total}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

// Relative time helper for the scheduler badge — "2h ago", "3 days ago", "in 2d", etc.
function fmtRelative(iso) {
  if (!iso) return "—";
  if (iso === "next-check") return "within 6h";
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((t - now) / 1000);
  const abs = Math.abs(diffSec);
  const future = diffSec > 0;
  let val;
  let unit;
  if (abs < 60) { val = abs; unit = "s"; }
  else if (abs < 3600) { val = Math.round(abs / 60); unit = "m"; }
  else if (abs < 86400) { val = Math.round(abs / 3600); unit = "h"; }
  else { val = Math.round(abs / 86400); unit = "d"; }
  return future ? `in ${val}${unit}` : `${val}${unit} ago`;
}

function fmtAbsolute(iso) {
  if (!iso || iso === "next-check") return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function SchedulerBadge({ scheduler, nextUp }) {
  const enabled = !!scheduler.enabled;
  const lastAt = scheduler.last_published_at;
  const lastTitle = scheduler.last_published_title;
  const nextEta = scheduler.next_auto_eta;

  return (
    <div
      data-testid="blog-scheduler-status"
      className={`flex flex-wrap items-stretch gap-3 mb-5 p-1 rounded-2xl border ${
        enabled ? "border-emerald-100 bg-emerald-50/40" : "border-amber-100 bg-amber-50/40"
      }`}
    >
      {/* Status pill */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-slate-100">
        <span
          className={`relative inline-flex w-2.5 h-2.5 rounded-full ${
            enabled ? "bg-emerald-500" : "bg-amber-500"
          }`}
        >
          {enabled && (
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
          )}
        </span>
        <div className="leading-tight">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Scheduler
          </div>
          <div className={`text-sm font-bold ${enabled ? "text-emerald-700" : "text-amber-700"}`}>
            {enabled ? "Auto-publishing" : "Paused"}
          </div>
        </div>
      </div>

      {/* Last published */}
      <div className="flex-1 min-w-[180px] flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl border border-slate-100">
        <div className="leading-tight min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Last published
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate" title={lastTitle || ""}>
            {lastTitle || "—"}
          </div>
          <div className="text-[11px] text-slate-500" title={fmtAbsolute(lastAt)}>
            {fmtRelative(lastAt)}
          </div>
        </div>
      </div>

      {/* Next up */}
      <div className="flex-1 min-w-[180px] flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl border border-slate-100">
        <div className="leading-tight min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Next in queue
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate" title={nextUp?.title || ""}>
            {nextUp?.title || "Queue empty 🎉"}
          </div>
          {nextUp && enabled && (
            <div className="text-[11px] text-slate-500" title={fmtAbsolute(nextEta)}>
              {nextEta === "next-check" ? "Publishing soon" : `Publishes ${fmtRelative(nextEta)}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
